import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { BreadcrumbsPath } from "components/commons/BreadscrumbsPath";
import { GIGYA_SUCCESS_CODE } from "constants/cdc";
import { useAuthUser } from "hooks/authentication";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import type React from "react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { STORAGE_KEYS, sessionStorageService } from "service/storage";
import { authenNavigate } from "service/utils/authen-navigate";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { CountrySelect } from "./CountrySelect";

export const ProfilePage = () => {
  authenNavigate();
  const [isSave, setIsSave] = useState(false);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const { t, i18n } = useTranslation();
  const cdcUser = getDataFromLocalstorageByKey("cdcUser");
  // Always default to 'en' regardless of CDC user's locale preference
  const [languageCode, setLanguageCode] = useState("en");
  const { settings, setSettings } =
    useContext<SettingsContextValue>(SettingsContext);

  const { user } = useAuthUser();
  const [profile, setProfile] = useState({
    firstName: cdcUser?.firstName ?? user?.given_name ?? "",
    lastName: cdcUser?.lastName ?? user?.family_name ?? "",
    company: cdcUser?.company ?? user?.company ?? "",
    country: cdcUser?.country ?? "",
  });

  const changePassword = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    (window as any).gigya?.accounts.showScreenSet({
      screenSet: "Default-ProfileUpdate",
      startScreen: "gigya-change-password-screen",
      onHide: (res: any) => {
        console.log("User close popup:", res);
      },
      onAfterSubmit: (res: any) => {
        if (res?.response?.errorCode === GIGYA_SUCCESS_CODE) {
          setIsPasswordUpdated(true);
          setTimeout(() => {
            setIsPasswordUpdated(false);
          }, 1000);
        }
      },
    });
  };

  const getAccountInfo = () => {
    return window.gigya?.accounts.getAccountInfo({
      extraProfileFields: "work",
      callback: (response) => {
        if (response?.profile) {
          const cdcUserResponse: any = {
            firstName: response?.profile?.firstName,
            lastName: response?.profile?.lastName,
            email: response?.profile?.email,
            country: response?.profile?.country,
            locale: response?.profile?.locale,
            company: (response?.data?.work as any)?.cov_company,
          };
          cdcUserResponse.extData = response?.data;
          localStorage.setItem("cdcUser", JSON.stringify(cdcUserResponse));
          setProfile(cdcUserResponse);
          // Always keep languageCode as 'en' - don't read from CDC user's locale
          // setLanguageCode(cdcUserResponse?.locale);
          if (!sessionStorageService.getItem(STORAGE_KEYS.RELOADED)) {
            sessionStorageService.setItem(STORAGE_KEYS.RELOADED, "true");
            window.location.reload(); // Reload once
          }
        }
      },
    });
  };

  const countryChange = (code?: string) => {
    if (code) setProfile({ ...profile, country: code });
  };

  const handleChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setSettings({
      ...settings,
      language: lang ? lang.toLocaleUpperCase() : "EN",
    });
  };

  const saveProfile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    window.gigya?.accounts.setAccountInfo({
      profile: {
        country: profile.country,
        firstName: profile.firstName,
        lastName: profile.lastName,
        locale: languageCode,
      },
      data: {
        work: {
          cov_company: profile.company,
        },
      },
      callback: () => {
        sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
        localStorage.removeItem("cdcUser");
        setIsSave(true);
        setTimeout(() => {
          setIsSave(false);
          handleChangeLanguage(languageCode);
          getAccountInfo();
        }, 1000);
      },
    });
  };

  return (
    <Grid
      container
      mt={0}
      mb={0}
      rowSpacing={3}
      columnSpacing={4}
      sx={{
        backgroundColor: "#fff",
        paddingLeft: { xs: "16px", sm: "24px", md: "64px", lg: "96px" },
        paddingRight: { xs: "16px", sm: "24px", md: "64px", lg: "96px" },
      }}
    >
      {isSave && (
        <Alert
          sx={{ position: "absolute", right: { xs: "16px", md: "0" } }}
          severity="success"
        >
          {t("pf_saved_successfully")}
        </Alert>
      )}
      {isPasswordUpdated && (
        <Alert
          sx={{ position: "absolute", right: { xs: "16px", md: "0" } }}
          severity="success"
        >
          {t("pf_password_changed_successfully")}
        </Alert>
      )}
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <BreadcrumbsPath pathName={t("pf_account_details")} />
      </Grid>
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <Box minHeight={"398px"} marginBottom={"24px"}>
          <Box
            sx={{
              width: { xs: "100%", md: "612px" },
              margin: "auto",
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontSize: "var(--body-b1-16-regular-size, 16px)",
                fontWeight: 400,
                lineHeight: "19.23px",
                letterSpacing: "0.15px",
                marginBottom: "16px",
              }}
              variant="h6"
              component="div"
            >
              {t("pf_login_data")}
            </Typography>
            <TextField
              sx={{
                "& input": { padding: "0 14px" },
                "& div": {
                  borderRadius: "4px",
                  height: "40px",
                  backgroundColor: "#f2f2f2",
                },
              }}
              fullWidth
              label=""
              id="loginData"
              value={cdcUser?.email ?? user?.email ?? ""}
              disabled
            />
            <Box sx={{ width: "100%", marginTop: "16px" }}>
              <Link
                href="#"
                onClick={changePassword}
                underline="none"
                sx={{ float: "right" }}
              >
                <Typography
                  sx={{
                    color: "#009FE4",
                    fontSize: "var(--body-b2-14-regular-size, 14px)",
                    fontWeight: 400,
                    lineHeight: "16.55px",
                  }}
                  component="span"
                >
                  {t("pf_change_password")}
                </Typography>
              </Link>
            </Box>
            <Box sx={{ width: "100%", marginTop: "64px" }}>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "var(--body-b1-16-regular-size, 16px)",
                  fontWeight: 400,
                  lineHeight: "19.23px",
                  letterSpacing: "0.15px",
                  marginBottom: "16px",
                }}
                variant="h6"
                component="div"
              >
                {t("pf_account_details")}
              </Typography>
              <Box
                component="form"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  "& .MuiTextField-root": {
                    minWidth: { xs: "100%", sm: "298px" },
                    height: "40px",
                    borderRadius: "4px",
                  },
                  "& div.MuiInputBase-root": {
                    borderRadius: "4px",
                  },
                }}
              >
                <TextField
                  sx={{
                    marginRight: "16px",
                  }}
                  required
                  id="outlined-required"
                  label={t("pf_first_name")}
                  value={profile.firstName}
                  onChange={(e) =>
                    setProfile({ ...profile, firstName: e.target.value })
                  }
                />
                <TextField
                  required
                  id="outlined-required"
                  label={t("pf_last_name")}
                  value={profile.lastName}
                  onChange={(e) =>
                    setProfile({ ...profile, lastName: e.target.value })
                  }
                />
                <TextField
                  sx={{ marginTop: "32px" }}
                  required
                  id="outlined-required"
                  label={t("pf_company")}
                  value={profile.company}
                  onChange={(e) =>
                    setProfile({ ...profile, company: e.target.value })
                  }
                />
              </Box>
            </Box>
            <Box
              sx={{
                width: "100%",
                marginTop: "32px",
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
              }}
            >
              <FormControl sx={{ marginRight: "16px", minWidth: "298px" }}>
                <CountrySelect
                  countryChange={countryChange}
                  country={profile.country}
                />
              </FormControl>
              <FormControl sx={{ minWidth: "298px" }}>
                <InputLabel id="select-language">{t("pf_language")}</InputLabel>
                <Select
                  sx={{ borderRadius: "4px" }}
                  labelId="select-language"
                  id="selectUserLanguage"
                  value={languageCode}
                  label={t("pf_language")}
                  onChange={(e) => setLanguageCode(e.target.value)}
                >
                  <MenuItem value="en">{t("language_english")}</MenuItem>
                  {/* Currently, we use only english
                  <MenuItem value="de">{t("language_german")}</MenuItem>
                  <MenuItem value="zh">{t("language_chinese")}</MenuItem> */}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: "100%", marginTop: "36px" }}>
              <Button
                onClick={saveProfile}
                sx={{
                  width: "124px",
                  height: "32px",
                  padding: "4px 15px",
                  fontSize: "var(--body-b2-14-regular-size, 14px)",
                  whiteSpace: "nowrap",
                  borderRadius: "4px",
                }}
                variant="contained"
              >
                {t("pf_save_change")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
